import { useEffect } from 'react';
import { FeedbackType } from '@weave/schema-gen-ts/dist/schemas/office-assistant/v1/office_assistant_service.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, Text, TextareaField, useForm } from '@frontend/design-system';

type TagFeedbackModalProps = ModalControlModalProps & {
  onSubmit: (feedback: string) => void;
  onCancel?: () => void;
  onSkip?: () => void;
  hideSkip?: boolean;
  title?: string;
  subtitle?: string;
  feedbackType: FeedbackType;
  trackingIdPrefix?: string;
};

export const TagFeedbackModal = ({
  onSubmit,
  onCancel,
  onSkip,
  hideSkip,
  title,
  subtitle,
  feedbackType,
  trackingIdPrefix,
  ...rest
}: TagFeedbackModalProps) => {
  const { t } = useTranslation('tag-selection');
  const { getFieldProps, seedValues, isComplete, formProps } = useForm({
    fields: {
      feedback: {
        type: 'text',
        value: '',
        required: true,
      },
    },
    onSubmit: (values) => {
      onSubmit(values.feedback ?? '');
    },
  });

  useEffect(() => {
    if (!rest.show) {
      seedValues({ feedback: '' });
    }
  }, [rest.show]);

  const baseTrackingId = (trackingIdPrefix ? `${trackingIdPrefix}-` : '') + `smart-tag-${feedbackType}`;

  return (
    <Modal maxWidth={525} {...rest} asChild>
      <form {...formProps}>
        <Modal.Header onClose={rest.onClose}>{title ?? t('Additional Feedback')}</Modal.Header>
        <Modal.Body>
          <Text css={{ marginBottom: theme.spacing(2) }}>
            {subtitle ?? t('This will help us improve your experience')}
          </Text>
          <TextareaField
            label={t('Feedback')}
            {...getFieldProps('feedback')}
            css={{
              width: 475,
              maxWidth: '100%',
            }}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer
          primary={{
            label: t('Submit Feedback'),
            type: 'submit',
            disabled: !isComplete,
            trackingId: `${baseTrackingId}-submit`,
          }}
          secondary={
            !!onSkip && !hideSkip
              ? {
                  label: t('Skip'),
                  onClick: onSkip,
                  trackingId: `${baseTrackingId}-skip`,
                }
              : undefined
          }
          tertiary={
            onCancel
              ? {
                  label: t('Cancel'),
                  onClick: onCancel,
                  trackingId: `${baseTrackingId}-cancel`,
                }
              : undefined
          }
        />
      </form>
    </Modal>
  );
};
